import type { SpaceWithAvailability } from '@store/models';

/**
 * Default capacity buffer added to the attendee count to define the
 * "comfortably-sized" recommendation range, e.g. attendees=25 → [25, 35].
 */
export const DEFAULT_RECOMMENDATION_BUFFER = 10;

/**
 * Stricter availability rule for the "Recommended" badge: a space is only a
 * recommendation candidate when every requested day is available (or no
 * per-day info was returned — treat as bookable). This is intentionally
 * stricter than `isSpaceBookable` (which only requires *some* day available);
 * we never recommend a partially-available space.
 */
function isFullyAvailable(space: SpaceWithAvailability): boolean {
  const days = space.spaceAvailability;
  if (!Array.isArray(days) || days.length === 0) return true;
  return days.every((d) => d.available);
}
/**
 * Computes the single best-match space ID that should display the
 * "Recommended" badge, given an attendee count and an optional capacity buffer.
 *
 * Algorithm:
 *   • Range = [attendeeCount, attendeeCount + buffer]
 *   • Consider only fully-available spaces with `roomCapacity >= attendeeCount`.
 *   • If one or more candidates have `roomCapacity` inside the range, recommend
 *     the single smallest-capacity space among them (best fit).
 *   • Otherwise (no candidate within the buffer), fall back to the single
 *     space with the smallest capacity strictly above the range.
 *   • Spaces with capacity below `attendeeCount` are NEVER recommended.
 *
 * Examples (buffer = 10):
 *   A) attendees=25, capacities=[28,35,44,100] → recommended = {28}
 *   B) attendees=25, capacities=[44,55,100]    → recommended = {44}
 *   C) attendees=25, capacities=[44]           → recommended = {44}
 *   D) attendees=25, capacities=[10,20]        → recommended = {} (none above)
 */
export function getRecommendedSpaceIds(
  spaces: readonly SpaceWithAvailability[],
  attendeeCount: number | undefined,
  buffer: number = DEFAULT_RECOMMENDATION_BUFFER
): Set<string> {
  const recommended = new Set<string>();
  if (attendeeCount === undefined || attendeeCount <= 0) {
    return recommended;
  }

  const effectiveBuffer = Math.max(0, buffer);
  const upperBound = attendeeCount + effectiveBuffer;

  const candidates = spaces.filter(
    (s) =>
      isFullyAvailable(s) && typeof s.roomCapacity === 'number' && s.roomCapacity >= attendeeCount
  );

  if (candidates.length === 0) {
    return recommended;
  }

  const inRange = candidates.filter((s) => (s.roomCapacity as number) <= upperBound);

  if (inRange.length > 0) {
    // Best fit from the comfortable range: smallest capacity.
    const best = inRange.reduce((a, b) =>
      (a.roomCapacity as number) <= (b.roomCapacity as number) ? a : b
    );
    recommended.add(best.spaceId);
    return recommended;
  }

  // Fallback: single space with the smallest capacity above the range.
  const best = candidates.reduce((a, b) =>
    (a.roomCapacity as number) <= (b.roomCapacity as number) ? a : b
  );
  recommended.add(best.spaceId);
  return recommended;
}
